/**
 * Kalender as a resource calendar (owner, 2026-09-21): people down the side, days across, and one
 * continuous block per absence rather than one square per day. Pure: which days are on screen,
 * the week bands above them, and where each person's blocks start and end.
 */
import type { ApiAbsence } from '@/api/data';
import { addDays, isoWeek, mondayOf } from '@timefly/core';
import { spanOf } from './dates';
import { lastOf, shiftMonth } from './stats';

export type View = 'uker' | 'maned' | 'neste7';
export const VIEWS: [View, string][] = [
  ['uker', '3 uker'],
  ['maned', 'Måned'],
  // the indicator «Fravær neste 7 dager» on Søknader opens this (#276)
  ['neste7', '7 dager'],
];

/**
 * Three weeks are last, this and next week around the anchor; a month is the anchor's month; seven
 * days start on the anchor itself, so from today they are today and the six after.
 */
export function daysFor(view: View, anchor: string): string[] {
  const from =
    view === 'uker' ? addDays(mondayOf(anchor), -7) : view === 'neste7' ? anchor : `${anchor.slice(0, 7)}-01`;
  const n = view === 'uker' ? 21 : view === 'neste7' ? 7 : Number(lastOf(anchor.slice(0, 7)).slice(8, 10));
  return Array.from({ length: n }, (_, i) => addDays(from, i));
}

/** Back or forward: a week at a time in three weeks and seven days, so the week just left stays in view; a month in Måned. */
export const stepFor = (view: View, anchor: string, n: number) =>
  view === 'maned' ? `${shiftMonth(anchor.slice(0, 7), n)}-01` : addDays(anchor, 7 * n);

/** The week bands over the days: "Uke 38" and how many of the days on screen are in it. */
export function weeksOf(days: string[]): { week: number; start: number; span: number }[] {
  const out: { week: number; start: number; span: number }[] = [];
  days.forEach((d, i) => {
    const w = isoWeek(d);
    const last = out[out.length - 1];
    if (last && last.week === w) last.span++;
    else out.push({ week: w, start: i, span: 1 });
  });
  return out;
}

export interface Block {
  a: ApiAbsence;
  /** first and last column on screen, 0-based and inclusive */
  start: number;
  end: number;
  /** the absence goes on beyond the left or the right edge */
  cutL: boolean;
  cutR: boolean;
}

/** One person's absences on screen, declined left out; a request is still drawn, dashed. */
export function blocksFor(absences: ApiAbsence[], uid: number, days: string[]): Block[] {
  const first = days[0] ?? '';
  const last = days[days.length - 1] ?? '';
  return absences
    .filter((a) => a.user_id === uid && a.status !== 'declined')
    .map((a) => ({ a, ...spanOf(a) }))
    .filter((x) => x.to >= first && x.from <= last)
    .sort((x, y) => x.from.localeCompare(y.from))
    .map(({ a, from, to }) => ({
      a,
      start: from < first ? 0 : days.indexOf(from),
      end: to > last ? days.length - 1 : days.indexOf(to),
      cutL: from < first,
      cutR: to > last,
    }));
}

/**
 * The same absences as a list (Fravær V1.4, #276): the phone's agenda and the desktop's full-name
 * alternative to the grid. Only the people on screen and the days on screen, declined left out, as
 * the grid; absences with the same period share a heading, earliest period first.
 */
export function agendaOf(
  absences: ApiAbsence[],
  uids: ReadonlySet<number>,
  days: string[],
): { from: string; to: string; items: ApiAbsence[] }[] {
  const first = days[0] ?? '';
  const last = days[days.length - 1] ?? '';
  const groups = new Map<string, { from: string; to: string; items: ApiAbsence[] }>();
  for (const a of absences) {
    if (!uids.has(a.user_id) || a.status === 'declined') continue;
    const { from, to } = spanOf(a);
    if (to < first || from > last) continue;
    const k = `${from}|${to}`;
    const g = groups.get(k) ?? { from, to, items: [] };
    g.items.push(a);
    groups.set(k, g);
  }
  return [...groups.values()].sort((x, y) => x.from.localeCompare(y.from) || x.to.localeCompare(y.to));
}
